// Inline "what is this?" affordance for the core navigation vocabulary
// (top nav + sidebar). Renders a small `?` glyph whose plain-language
// definition — from core-vocabulary.ts — rides the native `title` +
// `aria-label`.
//
// Mirrors web/src/cleanup/CleanupGlossaryTerm.tsx deliberately: same
// proven, no-clutter mechanism, just sourced from the core-vocabulary
// map instead of the Cleanup glossary.
//
// DECISION — native `title`, not the positioned <Tooltip> primitive from
// ui.tsx. The top nav's `.top-nav-list` is `overflow: hidden`, which
// clips an absolutely-positioned tooltip bubble — the brand bubble would
// render invisibly in the navy bar. The native title shows unclipped and
// identically on the navy top nav AND the light sidebar, is
// screen-reader accessible via aria-label, and degrades gracefully. This
// follows the CleanupGlossaryTerm pattern rather than inventing a new
// mechanism. (The <Tooltip> primitive stays the right tool elsewhere,
// where its container isn't clipping the bubble — e.g. the TopNav
// settings gear.)
//
// Unknown keys degrade to null (no glyph), so a typo never renders an
// empty affordance.

import { lookupCoreVocabulary } from "./core-vocabulary";

export function CoreVocabularyTip({
  k,
  className,
}: {
  k: string;
  className?: string;
}) {
  const entry = lookupCoreVocabulary(k);
  if (!entry) return null;
  return (
    <span
      role="note"
      tabIndex={0}
      aria-label={`${entry.term}: ${entry.definition}`}
      title={entry.definition}
      className={`nav-help-tip${className ? ` ${className}` : ""}`}
      // The `?` only explains — it never navigates. Stop a click from
      // bubbling to an ancestor row/handler (e.g. a sidebar pin row).
      // When the glyph sits inside a nav <Link>, the link's own
      // navigation is harmless (it points where the label points), so we
      // don't preventDefault.
      onClick={(e) => e.stopPropagation()}
    >
      ?
    </span>
  );
}
